import { DATE_FORMATS, PATHS } from '@/constants';
import { type KeyValueProperty } from '@/containers/IndividualScoreUpdateContainer';
import { type ScoringValidationFormData } from '@/containers/ScoringTemplateContainer/ScoringValidationContainer/scoringValidationFormData';
import {
  type AdjustableProductAndService,
  type ConfirmScoringMutationVariables,
  type FileInput,
  type GetLsdMutation,
  type GetLsdMutationVariables,
  type LsdFamily,
  type LsdScoreInput,
  type MatchedScore,
  MatchingReason,
  type Maybe,
  type MeQuery,
  type ProductServiceAdjustmentInput,
  type SaveScoresMutationVariables,
  type ScoredCandidate,
  ScoreStatusEnum,
  type ScoreType,
  type ScoringType,
  type ScoringValidationDefaultsQuery,
  type SessionLogisticsReceived,
  type StoredScoreQuery,
  type TestType,
  type UploadScoreMutationVariables,
  type UploadScoreSuccess,
} from '@/graphql';
import { formatDate } from '@/helpers/dateFormatter';
import { type AdjustableProductForm } from '@/hooks';
import { type LSDItem, type MappedCandidate, type MatchedCandidate, type ScoringContextModel } from '@/models';

const TOTAL_CALCULATED_SCORE_TYPE = 'totalCalculatedScore';
const TOTAL_RAW_SCORE_TYPE = 'totalRawScore';

export const getScoringData = (): ScoringContextModel => {
  const currentContext = sessionStorage.getItem('scoring_context');
  return !currentContext
    ? {
        sessionId: null,
        matchedCandidates: [],
        scoredCandidates: [],
      }
    : JSON.parse(currentContext);
};

export const updateScoringData = (payload: ScoringContextModel): void => {
  const newContext = {
    ...getScoringData(),
    ...payload,
  };
  sessionStorage.setItem('scoring_context', JSON.stringify(newContext));
};

export const clearScoringData = (): void => {
  sessionStorage.setItem(
    'scoring_context',
    JSON.stringify({
      sessionId: null,
      matchedCandidates: [],
      scoredCandidates: [],
    })
  );
};

const buildScoreValue = ({ value = 0 }: { value?: Maybe<number> | undefined }): string | number =>
  !value ? 'NA' : value;

const buildScoreShortType = (score: ScoreType): string =>
  score.type
    .split(/(?=[A-Z])/)
    .filter((currentWord) => !['and', 'calculated', 'score'].includes(currentWord.toLowerCase()))
    .reduce((shortTypeName, currentWord) => `${shortTypeName}${currentWord.charAt(0).toUpperCase()}`, '');

export const transformScore = (scores?: ScoreType[] | null, totalScore?: number): string => {
  if (!scores?.length) {
    return '';
  }
  const details = scores?.map((score) => `${buildScoreShortType(score)}-${buildScoreValue(score)}`).join(' + ');
  return `${details} = ${buildScoreValue({ value: totalScore })}`;
};

export const getIopsFileWarningForSessionWithoutAcceptedAppointments = (
  numberOfScoredCandidates: number,
  expectedRows: number
): { translationKey: string; params: Record<string, string | number> } | null => {
  if (expectedRows === numberOfScoredCandidates) {
    return null;
  }

  return {
    translationKey: 'pages.scoring.quality.warning_iops_file',
    params: {
      nbAnswers: expectedRows,
      nbRows: numberOfScoredCandidates,
    },
  };
};

export const getIopsFileWarning = (
  numberOfAcceptedAppointments: number,
  numberOfScoredCandidates: number,
  expectedRows: number,
  shouldBeManagedInTasPortal?: boolean
): { translationKey: string; params: Record<string, string | number> } | null => {
  if (getScoringData().isNumberMode ?? !shouldBeManagedInTasPortal) {
    return getIopsFileWarningForSessionWithoutAcceptedAppointments(numberOfScoredCandidates, expectedRows);
  }

  return getIopsFileWarningForSessionWithAcceptedAppointments(
    numberOfScoredCandidates,
    numberOfAcceptedAppointments,
    expectedRows
  );
};

const getIopsFileWarningForSessionWithAcceptedAppointments = (
  numberOfScoredCandidates: number,
  numberOfAcceptedAppointments: number,
  expectedRows: number
): { translationKey: string; params: Record<string, string | number> } | null => {
  if (expectedRows === numberOfScoredCandidates && expectedRows === numberOfAcceptedAppointments) {
    return null;
  }

  return {
    translationKey: 'pages.scoring.quality.warning_iops_file_session_with_appointments',
    params: {
      nbAnswers: expectedRows,
      nbRows: numberOfScoredCandidates,
      nbAcceptedAppointments: numberOfAcceptedAppointments,
    },
  };
};

export const getIopsFileWithWrongFormVersionWarning = (
  sessionFormVersion?: string | null,
  iopsFormCodes?: string[]
): { translationKey: string; params?: Record<string, string | number> } | null => {
  if (!iopsFormCodes || !sessionFormVersion || !iopsFormCodes.length) {
    return null;
  }

  const wrongFormCodes = iopsFormCodes.filter((formCode) => formCode !== sessionFormVersion);

  if (!wrongFormCodes.length) {
    return null;
  }

  return { translationKey: 'pages.scoring.quality.warning_iops_file_with_wrong_form_code' };
};

export const getIopsFileWithIncompleteCandidateNameWarning = (
  scoredCandidates: ScoredCandidate[] = []
): { translationKey: string; params?: Record<string, string | number> } | undefined => {
  for (const candidate of scoredCandidates) {
    if (!candidate.firstName || !candidate.lastName) {
      return { translationKey: 'pages.scoring.quality.warning_iops_file_with_incomplete_candidate_name' };
    }
  }
};

export const getDataAfterUpload = (
  uploadScoreResult: UploadScoreSuccess,
  variables: UploadScoreMutationVariables,
  nbAnswers: SessionLogisticsReceived['quantityAnswerSheetsToBeScored'],
  canHaveExternalCandidateIdInIopsFile?: boolean
): ScoringContextModel | null => {
  const { sessionCandidates, scoredCandidates } = uploadScoreResult;
  const matchedCandidates = sessionCandidates.reduce<MatchedCandidate[]>(
    (currentMatchedCandidates, currentSessionCandidate) => {
      const scoredCandidate = scoredCandidates.find(
        (currentScoredCandidate) => currentScoredCandidate?.rowNumber === currentSessionCandidate.matching?.rowNumber
      );

      currentMatchedCandidates.push({
        identificationId: currentSessionCandidate.identificationId,
        fullName: currentSessionCandidate.fullName,
        dateOfBirthAtRegistration: currentSessionCandidate.dateOfBirth,
        firstName: scoredCandidate?.firstName,
        lastName: scoredCandidate?.lastName,
        dateOfBirth: scoredCandidate?.dateOfBirth,
        scoredCandidateFullName: scoredCandidate?.fullName,
        scores: scoredCandidate?.scores,
        totalScore: scoredCandidate?.totalScore,
        match: currentSessionCandidate.matching
          ? {
              rowNumber: currentSessionCandidate.matching.rowNumber,
              reason: currentSessionCandidate.matching.reason,
              externalCandidateId: currentSessionCandidate.matching.externalCandidateId,
            }
          : null,
      });

      return currentMatchedCandidates;
    },
    []
  );
  return {
    iopsFileWithWrongFormCodeWarning: getIopsFileWithWrongFormVersionWarning(
      getScoringData().sessionFormVersion,
      scoredCandidates.map((scoredCandidate) => scoredCandidate.formCode)
    ),
    iopsFileWarning: getIopsFileWarning(
      matchedCandidates.length,
      scoredCandidates.length,
      Number(nbAnswers),
      getScoringData().shouldBeManagedInTasPortal
    ),
    iopsFileWithIncompleteCandidateNameWarning: getIopsFileWithIncompleteCandidateNameWarning(scoredCandidates),
    scoredCandidates,
    matchedCandidates:
      matchedCandidates.length === 0
        ? scoredCandidates.map(
            (scoredCandidate) =>
              ({
                identificationId: scoredCandidate.identificationId ?? '',
                fullName: scoredCandidate.fullName ?? '',
                dateOfBirth: scoredCandidate.dateOfBirth,
                scoredCandidateFullName: scoredCandidate.fullName,
                scores: scoredCandidate.scores,
                totalScore: scoredCandidate.totalScore,
                match: {
                  rowNumber: scoredCandidate.rowNumber,
                  reason: MatchingReason.Custom,
                  externalCandidateId: canHaveExternalCandidateIdInIopsFile ? scoredCandidate.identificationId : null,
                },
              } satisfies MatchedCandidate)
          )
        : matchedCandidates,
    uploadedFile: {
      name: variables.file.name,
      mimeType: variables.file.mimeType,
      content: variables.file.content,
    },
  };
};

export const getSaveScorePayload = (
  scoringData?: ScoringContextModel,
  sessionId?: string,
  currentUser?: MeQuery['me']
): SaveScoresMutationVariables => ({
  file: scoringData?.uploadedFile ?? ({} as unknown as FileInput),
  businessId: Number(currentUser?.businessId),
  sessionId: Number(sessionId),
  matchedScores: scoringData?.matchedCandidates
    ? (scoringData?.matchedCandidates
        .filter((matchedCandidate) => matchedCandidate.match?.rowNumber)
        .map((matchedCandidate) => ({
          identificationId: scoringData.isNumberMode ? null : matchedCandidate.identificationId,
          rowNumber: matchedCandidate?.match?.rowNumber,
          externalCandidateId: matchedCandidate?.match?.externalCandidateId,
          scoreStatus: matchedCandidate.status,
        })) as MatchedScore[])
    : ([] as MatchedScore[]),
  businessName: scoringData?.businessName ?? '',
});
export const getLsdMutationPayload = (
  sessionId?: string | number,
  sessionEndAt?: string | null,
  testType?: TestType,
  scoredCandidates?: Array<UploadScoreSuccess['scoredCandidates'][0]>,
  currentUser?: MeQuery['me']
): GetLsdMutationVariables => ({
  sessionId: Number(sessionId),
  businessId: Number(currentUser?.businessId),
  lsdInput: {
    criteria: {
      lsdFamily: testType?.lsdFamily as LsdFamily,
      threshold: testType?.lsdThreshold ?? 0,
    },
    scores: (scoredCandidates
      ? scoredCandidates.map((scoredCandidate) => ({
          rowNumber: scoredCandidate.rowNumber,
          totalScore: scoredCandidate.totalScore,
          candidate: {
            firstName: scoredCandidate.firstName,
            lastName: scoredCandidate.lastName,
            dateOfBirth: scoredCandidate.dateOfBirth,
          },
        }))
      : []) as LsdScoreInput[],
    sessionEndAt: sessionEndAt ?? '',
  },
});

export const getLargeScoreDifferenceDataPayload = (
  matchedCandidates?: MatchedCandidate[],
  largeScoreDifferenceData?: LSDItem[]
): { matchedCandidates?: MatchedCandidate[] } => ({
  matchedCandidates: matchedCandidates?.map((matchedCandidate) => {
    const hasLSD = !!largeScoreDifferenceData?.find(
      (lsdItem) => lsdItem.rowNumber === matchedCandidate?.match?.rowNumber
    );
    return {
      ...matchedCandidate,
      status: ScoreStatusEnum.UnderAdminReview,
      hasLSD,
    };
  }),
});

export const mapLsdData = (
  scoredCandidates?: Array<UploadScoreSuccess['scoredCandidates'][0]>,
  lsdResponse?: GetLsdMutation
): LSDItem[] | undefined =>
  lsdResponse?.lsd.map((item) => {
    const contextCandidateData = scoredCandidates?.find(
      (scoredCandidate) => item.rowNumber === scoredCandidate.rowNumber
    );

    return {
      rowNumber: item.rowNumber,
      identificationId: contextCandidateData?.identificationId as string,
      candidate: { ...item.candidate },
      currentScore: contextCandidateData ? [...contextCandidateData?.scores] : [],
      totalScore: contextCandidateData?.totalScore ?? 0,
      lsd: {
        ...item,
      },
    } satisfies LSDItem;
  });

export const transformScoreWithStoredTotal = (scores?: null | ScoreType[]): string => {
  if (!scores?.length) {
    return '';
  }
  return transformScore(
    scores.filter(({ type }) => ![TOTAL_CALCULATED_SCORE_TYPE, TOTAL_RAW_SCORE_TYPE].includes(type)),
    scores.find(({ type }) => type === TOTAL_CALCULATED_SCORE_TYPE)?.value ?? 0
  );
};

export const sortScoresByMatchingPriorityAndName = (a: MatchedCandidate, b: MatchedCandidate): number => {
  if (a.priority === undefined || b.priority === undefined) return 1;

  if (
    a.priority > b.priority ||
    (a.priority === b.priority &&
      a.scoredCandidateFullName &&
      b.scoredCandidateFullName &&
      a.scoredCandidateFullName?.toLowerCase() < b.scoredCandidateFullName?.toLowerCase())
  ) {
    return -1;
  }
  return 1;
};

export const getAvailableCandidatesToMatchFromSelected = (
  context: Pick<ScoringContextModel, 'scoredCandidates' | 'matchedCandidates'>,
  rowNumber?: number
): ScoringContextModel['scoredCandidates'] => {
  const matchedRowNumbers = context?.matchedCandidates
    ?.filter((matchedCandidate) => matchedCandidate.match)
    .map((candidate) => candidate?.match?.rowNumber);
  return context?.scoredCandidates?.filter(
    (scoredCandidate) =>
      !matchedRowNumbers?.includes(scoredCandidate.rowNumber) || rowNumber === scoredCandidate.rowNumber
  );
};

export const scoreIsMatched = (mappedCandidate: MappedCandidate): boolean =>
  !!mappedCandidate.storedScore && !!mappedCandidate.storedScore?.details;

export const getOrderAdjustmentFieldName = (productAndServiceModel: AdjustableProductAndService): string => {
  const productId = productAndServiceModel?.id ?? 0;
  return `productAndService-${productId}`;
};

export const getMappedOrderAdjustmentsData = (
  scoringValidationDefaultsQueryResult: ScoringValidationDefaultsQuery
): AdjustableProductForm => {
  const mappedOrderAdjustmentsData: AdjustableProductForm = {};
  scoringValidationDefaultsQueryResult?.scoringValidationDefaults?.adjustedProducts?.forEach(
    (productAndServiceModel) => {
      const fieldName = getOrderAdjustmentFieldName(productAndServiceModel as AdjustableProductAndService);
      mappedOrderAdjustmentsData[fieldName] = {
        ...(productAndServiceModel as AdjustableProductAndService),
      };
    }
  );
  return mappedOrderAdjustmentsData;
};

export const getConfirmScorePayload = (
  formValues: ScoringValidationFormData,
  businessId: number | null,
  scoringData: ScoringContextModel,
  sessionId?: string | null,
  isPurchaseEditable?: boolean
): ConfirmScoringMutationVariables | null => {
  let confirmScoringPayload: ConfirmScoringMutationVariables = {
    businessId: Number(businessId) ?? 0,
    scoredAt: formatDate(formValues.scoredAt, DATE_FORMATS.FULL_DATE_TO_SEND),
    scorerId: Number(formValues.scorer),
    sessionId: Number(sessionId),
  };

  if (!scoringData.isOpen && scoringData.isCmd) {
    confirmScoringPayload = {
      ...confirmScoringPayload,
      plannedAt: formatDate(formValues.plannedAt, DATE_FORMATS.FULL_DATE_TO_SEND),
      scoringType: scoringData?.scoringType as ScoringType,
      purchaseId: Number(scoringData.purchase?.id),
    };
  }
  if (!scoringData.isOpen && !scoringData.isCmd) {
    const productAndServiceAdjustmentsInformation: ProductServiceAdjustmentInput[] = [];
    Object.values(formValues.orderAdjustmentsData).forEach((prodAndServiceData) => {
      productAndServiceAdjustmentsInformation.push({
        productAndService: Number(prodAndServiceData.id),
        quantity: Number(prodAndServiceData.quantity),
      });
    });
    confirmScoringPayload = {
      ...confirmScoringPayload,
      plannedAt: formatDate(formValues.plannedAt, DATE_FORMATS.FULL_DATE_TO_SEND),
      scoringType: scoringData?.scoringType as ScoringType,
      purchaseId: Number(scoringData.purchase?.id),
    };

    if (isPurchaseEditable) {
      confirmScoringPayload = {
        ...confirmScoringPayload,
        purchaseAdjustment: {
          numberOfCandidatesToInvoice: formValues.realCandidatesNo,
          productAndServiceAdjustmentsInformation,
        },
      };
    }
  }
  return confirmScoringPayload;
};

export const getScoringProcessPaths = (
  scoringPath: string,
  sessionId?: string | number,
  scoreId?: number,
  queryParams?: string
): string => {
  const sessionIdForPath = Number(sessionId);
  switch (scoringPath) {
    case PATHS.QUALITY_CONTROL:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.QUALITY_CONTROL}`;
    case PATHS.SCORE_MATCHING:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.SCORE_MATCHING}`;
    case PATHS.CANDIDATES_SCORE_DISPLAY:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.CANDIDATES_SCORE_DISPLAY}`;
    case PATHS.SCORING_VALIDATION:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.SCORING_VALIDATION}`;
    case PATHS.CANDIDATE_OPTIONS:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.CANDIDATE_OPTIONS}`;
    case PATHS.SCORE_EDIT:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.SCORE_EDIT ?? ''}${queryParams ?? ''}`;
    case PATHS.SCORE_UPDATE:
      return `${PATHS.SCORING}${sessionIdForPath}/${PATHS.SCORE_UPDATE}${Number(scoreId)}`;
    default:
      return `${PATHS.SESSION_DETAILS}/${sessionIdForPath}`;
  }
};

export const mapStoredScored = (storedScoreResult?: StoredScoreQuery): KeyValueProperty[] | null => {
  if (!storedScoreResult) {
    return null;
  }
  const { storedScore } = storedScoreResult;
  return [
    {
      key: 'field.first_name',
      label: 'pages.scoring.score_update.field.first_name',
      value: storedScore.candidate.nameAtExam?.firstName ?? '',
    },
    {
      key: 'field.last_name',
      label: 'pages.scoring.score_update.field.last_name',
      value: storedScore.candidate.nameAtExam?.lastName ?? '',
    },
    {
      key: 'field.date_of_birth',
      label: 'pages.scoring.score_update.field.date_of_birth',
      value: formatDate(storedScore.candidate?.dateOfBirth, DATE_FORMATS.DATE_ONLY),
    },
    {
      key: 'field.candidate_id',
      label: 'pages.scoring.score_update.field.candidate_id',
      value: storedScore.candidate?.identificationId ?? '',
    },
    {
      key: 'field.scores',
      label: 'pages.scoring.score_update.field.scores',
      value: transformScoreWithStoredTotal(storedScore?.details as ScoreType[]),
    },
    {
      key: 'field.result_number',
      label: 'pages.scoring.score_update.field.result_number',
      value: storedScore?.iopsScore?.resultNumber?.toString() ?? '',
    },
    {
      key: 'field.form_version',
      label: 'pages.scoring.score_update.field.form_version',
      value: storedScore?.test?.formVersion ?? '',
    },
  ];
};

export const mapScoredCandidate = (scoredCandidate?: Partial<ScoredCandidate>): KeyValueProperty[] => [
  {
    key: 'field.first_name',
    value: scoredCandidate?.firstName ?? '',
  },
  {
    key: 'field.last_name',
    value: scoredCandidate?.lastName ?? '',
  },
  {
    key: 'field.date_of_birth',
    value: formatDate(scoredCandidate?.dateOfBirth, DATE_FORMATS.DATE_ONLY),
  },
  {
    key: 'field.candidate_id',
    value: scoredCandidate?.identificationId ?? '',
  },
  {
    key: 'field.scores',
    value: transformScore(scoredCandidate?.scores, scoredCandidate?.totalScore),
  },
  {
    key: 'field.result_number',
    value: scoredCandidate?.resultNumber?.toString() ?? '',
  },
  {
    key: 'field.form_version',
    value: scoredCandidate?.formCode ?? '',
  },
];
